import { authFetch } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import type { AIMessage, ToolCall } from '@/types/ai-assistant.types';

export interface AssistantChatRequest {
  message: string;
  history?: Array<{ role: string; content: string }>;
  context?: string;
}

export interface AssistantChatResponse {
  id: string;
  content: string;
  timestamp: string;
  toolCall?: {
    id: string;
    name: string;
    status: string;
    result?: unknown;
  };
}

export interface AIAssistantSessionDto {
  id: string;
  userId: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface AIAssistantSessionDetailDto {
  id: string;
  userId: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messages: AIAssistantMessageDto[];
}

export interface AIAssistantMessageDto {
  id: string;
  role: string;
  content: string;
  timestamp: number;
  toolCall?: {
    id: string;
    name: string;
    status: string;
    result?: unknown;
  };
}

export interface CreateSessionRequest {
  title?: string;
}

export interface AddMessageRequest {
  role: string;
  content: string;
  timestamp: number;
  toolCall?: {
    id: string;
    name: string;
    status: string;
    result?: unknown;
  };
}

class AIAssistantService {
  private readonly baseUrl = '/api/assistant';

  async sendMessage(request: AssistantChatRequest, signal?: AbortSignal): Promise<AssistantChatResponse> {
    const apiUrl = getApiUrl();
    const response = await authFetch(`${apiUrl}${this.baseUrl}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ?? `Failed to send message: ${response.status}`
      );
    }

    return response.json();
  }

  async checkHealth(): Promise<boolean> {
    try {
      const apiUrl = getApiUrl();
      const response = await authFetch(`${apiUrl}${this.baseUrl}/health`);
      return response.ok;
    } catch {
      return false;
    }
  }

  convertToAIMessage(response: AssistantChatResponse, role: 'assistant' | 'user' = 'assistant'): AIMessage {
    const message: AIMessage = {
      id: response.id,
      role,
      content: response.content,
      timestamp: new Date(response.timestamp).getTime(),
    };

    if (response.toolCall) {
      message.toolCall = {
        id: response.toolCall.id,
        name: response.toolCall.name,
        status: response.toolCall.status as ToolCall['status'],
        result: response.toolCall.result,
      };
    }

    return message;
  }

  buildHistoryFromMessages(messages: AIMessage[]): Array<{ role: string; content: string }> {
    return messages
      .filter(msg => msg.role === 'user' || msg.role === 'assistant')
      .map(msg => ({
        role: msg.role,
        content: msg.content,
      }));
  }

  async getSessions(): Promise<AIAssistantSessionDto[]> {
    const apiUrl = getApiUrl();
    const response = await authFetch(`${apiUrl}${this.baseUrl}/sessions`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to get sessions: ${response.status}`);
    }

    return response.json();
  }

  async getSession(sessionId: string): Promise<AIAssistantSessionDetailDto> {
    const apiUrl = getApiUrl();
    const response = await authFetch(`${apiUrl}${this.baseUrl}/sessions/${sessionId}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to get session: ${response.status}`);
    }

    return response.json();
  }

  async createSession(request?: CreateSessionRequest): Promise<AIAssistantSessionDto> {
    const apiUrl = getApiUrl();
    const response = await authFetch(`${apiUrl}${this.baseUrl}/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request ?? {}),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to create session: ${response.status}`);
    }

    return response.json();
  }

  async addMessage(sessionId: string, request: AddMessageRequest): Promise<AIAssistantMessageDto> {
    const apiUrl = getApiUrl();
    const response = await authFetch(`${apiUrl}${this.baseUrl}/sessions/${sessionId}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to add message: ${response.status}`);
    }

    return response.json();
  }

  async deleteSession(sessionId: string): Promise<void> {
    const apiUrl = getApiUrl();
    const response = await authFetch(`${apiUrl}${this.baseUrl}/sessions/${sessionId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to delete session: ${response.status}`);
    }
  }
}

export const aiAssistantService = new AIAssistantService();
